// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import type {
  CanonicalUnit,
  PantryConfirmedCandidate,
  PantryExtractionResponse,
} from "@bitebalance/contracts"
import { useI18n } from "@/i18n"
import { unitLabel } from "@/lib/units"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/onboarding/Checkbox"

export interface CandidateConfirmSheetProps {
  extraction: PantryExtractionResponse
  confirming: boolean
  /** Only passed by the scan flow — the real product photo Open Food Facts returned. */
  productImageUrl?: string | null
  /** Pre-fills every candidate's expiry from a date already chosen in the structured
   *  manual-entry form — only meaningful with a single candidate, the only case that
   *  form ever produces. */
  initialExpiryDate?: string | null
  onConfirm: (candidates: PantryConfirmedCandidate[]) => void
  onCancel: () => void
}

interface RowState {
  include: boolean
  normalizedQuantity: number
  normalizedUnit: CanonicalUnit
  storageLocation: string
  expiryDate: string
  showExpiry: boolean
}

/**
 * Editable candidate confirmation sheet (T13): match, quantity/unit,
 * confidence. Kitchen redesign (2026-09-14): expiry date is now a real,
 * optional field here — the confirmation payload already carried
 * `expiryDate`; only the control to set it was missing. Collapsed by
 * default, same as the mobile twin — most items don't need one.
 */
export function CandidateConfirmSheet({
  extraction,
  confirming,
  productImageUrl,
  initialExpiryDate,
  onConfirm,
  onCancel,
}: CandidateConfirmSheetProps) {
  const { t } = useI18n()
  const [rows, setRows] = useState<Record<string, RowState>>(() =>
    Object.fromEntries(
      extraction.candidates.map(c => [
        c.candidateId,
        {
          include: true,
          normalizedQuantity: c.normalizedQuantity,
          normalizedUnit: c.normalizedUnit,
          storageLocation: "",
          expiryDate: initialExpiryDate ?? "",
          showExpiry: !!initialExpiryDate,
        },
      ])
    )
  )

  function patchRow(id: string, patch: Partial<RowState>) {
    setRows(prev => ({ ...prev, [id]: { ...prev[id]!, ...patch } }))
  }

  function handleConfirm() {
    const payload: PantryConfirmedCandidate[] = extraction.candidates.map(c => {
      const row = rows[c.candidateId]!
      return {
        candidateId: c.candidateId,
        include: row.include,
        canonicalIngredientId: c.canonicalIngredientId,
        normalizedQuantity: row.normalizedQuantity,
        normalizedUnit: row.normalizedUnit,
        storageLocation: row.storageLocation.trim() || null,
        expiryDate: row.expiryDate.trim() || null,
      }
    })
    onConfirm(payload)
  }

  return (
    <Sheet open onOpenChange={next => !next && onCancel()}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          {productImageUrl ? (
            <div className="flex items-center gap-3">
              <img
                src={productImageUrl}
                alt=""
                className="size-10 rounded-bb-md bg-cream object-contain"
              />
              <p className="text-sm font-medium text-ink">{t("views.pantry.scanFoundTitle")}</p>
            </div>
          ) : null}
          <SheetTitle>{t("views.pantry.candidatesTitle")}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-3 px-4 pb-4">
          <p className="text-sm text-ink-2">{t("views.pantry.candidatesBody")}</p>
          <ul className="flex flex-col gap-3">
            {extraction.candidates.map(candidate => {
              const row = rows[candidate.candidateId]!
              const matched = candidate.canonicalIngredientId !== null
              return (
                <li
                  key={candidate.candidateId}
                  className="flex flex-col gap-2 rounded-bb-lg border border-line bg-cream p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <label className="flex items-center gap-2 font-medium text-ink">
                      <Checkbox
                        id={`cand-${candidate.candidateId}`}
                        checked={row.include}
                        onCheckedChange={checked =>
                          patchRow(candidate.candidateId, { include: checked })
                        }
                      />
                      {candidate.displayName}
                    </label>
                    <span
                      className={`rounded-bb-full px-2 py-0.5 text-xs font-medium ${
                        matched ? "bg-green/10 text-green" : "bg-gold/20 text-ink-2"
                      }`}
                    >
                      {matched ? t("views.pantry.matchedLabel") : t("views.pantry.unmatchedLabel")}
                    </span>
                  </div>
                  <p className="text-xs text-ink-2">
                    {t("views.pantry.confidenceLabel", {
                      percent: Math.round(candidate.confidence * 100),
                    })}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1 text-sm text-ink-2">
                      {t("views.pantry.quantityLabel")}
                      <Input
                        type="number"
                        min={0}
                        step="any"
                        className="w-24"
                        value={row.normalizedQuantity}
                        disabled={!row.include}
                        onChange={e =>
                          // WS6 negative-stock guard (ADR-062 §6): stock is added, never
                          // subtracted, through a confirmation — a negative entry becomes 0.
                          patchRow(candidate.candidateId, {
                            normalizedQuantity: Math.max(0, Number(e.target.value) || 0),
                          })
                        }
                      />
                    </label>
                    <select
                      className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
                      value={row.normalizedUnit}
                      disabled={!row.include}
                      onChange={e =>
                        patchRow(candidate.candidateId, {
                          normalizedUnit: e.target.value as CanonicalUnit,
                        })
                      }
                    >
                      <option value="g">g</option>
                      <option value="ml">ml</option>
                      <option value="count">{unitLabel("count", t)}</option>
                    </select>
                  </div>
                  {row.include ? (
                    row.showExpiry ? (
                      <label className="flex items-center gap-2 text-sm text-ink-2">
                        {t("views.pantry.expiryOptionalLabel")}
                        <Input
                          type="date"
                          className="w-40"
                          value={row.expiryDate}
                          min={new Date().toISOString().slice(0, 10)}
                          onChange={e =>
                            patchRow(candidate.candidateId, { expiryDate: e.target.value })
                          }
                        />
                      </label>
                    ) : (
                      <button
                        type="button"
                        className="self-start text-xs font-semibold text-green underline"
                        onClick={() => patchRow(candidate.candidateId, { showExpiry: true })}
                      >
                        {t("views.pantry.addExpiryButton")}
                      </button>
                    )
                  ) : null}
                </li>
              )
            })}
          </ul>
        </div>
        <SheetFooter>
          <Button type="button" className="min-h-11" disabled={confirming} onClick={handleConfirm}>
            {confirming ? t("views.pantry.confirming") : t("views.pantry.confirmButton")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            disabled={confirming}
            onClick={onCancel}
          >
            {t("views.pantry.cancelButton")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
